import { t } from "@lingui/core/macro";
import { useAtom, useAtomValue, useStore } from "jotai";
import { useEffect, useRef } from "react";

import { slotIdsToLockSelectorAtom } from "@/features/BookingWizard/atoms/selectors/slotIdsToLockAtom/slotIdsToLockAtom";
import {
  confirmedSlotLockKeyAtom,
  isBookingWizardClosingAtom,
} from "@/features/BookingWizard/atoms/stores";
import { useLockSlots } from "@/features/LockSlots";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

/**
 *
 * The SlotLockManager is responsible for locking the slots for the booking wizard.
 * It is only used for Duration based bookings.
 *
 * It listens for a duration change OR for course ID change -- and locks the relevant slots
 * for us -- ensuring that we lock the correct starting slot as well as all slots in the duration
 */
export const SlotLockManager = ({
  onClose,
  initiallyLockedSlotIds = [],
}: {
  onClose: () => void;
  initiallyLockedSlotIds?: readonly string[];
}) => {
  const [lockSlots, isLocking] = useLockSlots();
  const { showToast } = useToast();
  const store = useStore();

  const slotIdsToLock = useAtomValue(slotIdsToLockSelectorAtom);
  const [confirmedSlotLockKey, setConfirmedSlotLockKey] = useAtom(
    confirmedSlotLockKeyAtom
  );
  const initialLockKey = [...initiallyLockedSlotIds].sort().join(",") || null;
  const lastRequestedSlotIds = useRef<string | null>(
    confirmedSlotLockKey ?? initialLockKey
  );

  useEffect(() => {
    if (initialLockKey) {
      setConfirmedSlotLockKey((current) => current ?? initialLockKey);
    }
  }, [initialLockKey, setConfirmedSlotLockKey]);

  useEffect(() => {
    if (!slotIdsToLock || isLocking || store.get(isBookingWizardClosingAtom)) {
      return;
    }

    const selectedSlotIds = slotIdsToLock.split(",");
    const selectedLockKey = [...selectedSlotIds].sort().join(",");
    if (lastRequestedSlotIds.current === selectedLockKey) {
      return;
    }

    const timeout = setTimeout(() => {
      if (store.get(isBookingWizardClosingAtom)) {
        return;
      }

      lastRequestedSlotIds.current = selectedLockKey;
      setConfirmedSlotLockKey(null);
      lockSlots(
        {
          timeSlotIds: selectedSlotIds,
        },
        () => setConfirmedSlotLockKey(selectedLockKey),
        () => {
          showToast({
            title: t`Sorry we had a problem locking your slots.`,
            description: t`We'll go ahead and reload for you so you can try again.`,
            semanticStyle: "error",
            toastId: "tee-time-error",
          });
          onClose();
        }
      );
    }, 100);

    return () => clearTimeout(timeout);
  }, [
    isLocking,
    lockSlots,
    onClose,
    setConfirmedSlotLockKey,
    showToast,
    slotIdsToLock,
    store,
  ]);
  return null;
};
